import type { CalendarEventExternal } from '@schedule-x/calendar';
import clsx from 'clsx';
import { createContext, useContext } from 'react';
import { Temporal } from 'temporal-polyfill';

import { EventScheduleAnchor } from './session-schedule-calendar-event-popover';
import {
  EventContext,
  coachCalendarEventSurfaceClasses,
  coachCalendarTimeGridSessionGapClasses,
} from './session-schedule-calendar-shared';
import type { ConcreteEvent, ExpandedEvent } from './session-schedule-calendar-types';
import { expandedEventFromScheduleX, isoDateFromJsDate, scheduleXPlainDateFromString } from './schedule-x-temporal';

/**
 * Lookup for custom event cells. Schedule-X does not pass our `ConcreteEvent` into
 * `timeGridEvent` / `monthGridEvent`; context avoids a module-level mutable ref.
 */
export const ScheduleXEventsByIdContext = createContext<Map<string, ConcreteEvent>>(new Map());

function scheduleXEventCellClassName(
  event: ExpandedEvent,
  options: { variant: 'month' | 'time-grid' | 'date-grid'; calendarView?: 'month' | 'week' | 'day' },
): string {
  const endMs = event.end?.getTime();
  const isPast =
    endMs != null &&
    Temporal.Instant.compare(Temporal.Instant.fromEpochMilliseconds(endMs), Temporal.Now.instant()) < 0;

  if (options.variant === 'month') {
    return clsx(
      'w-full !outline-none px-[8px] text-left h-auto py-[2px] !border-none',
      coachCalendarEventSurfaceClasses(event.coachSessionRemarksV2, isPast),
    );
  }

  if (options.variant === 'date-grid') {
    return clsx(
      'w-full !outline-none px-[8px] text-left h-auto min-h-0 py-[2px] !border-none',
      coachCalendarEventSurfaceClasses(event.coachSessionRemarksV2, isPast),
    );
  }

  return clsx(
    'w-full !outline-none px-[8px] text-left h-full',
    coachCalendarTimeGridSessionGapClasses,
    options.calendarView === 'day' ? 'py-[4px]' : 'py-[8px]',
    coachCalendarEventSurfaceClasses(event.coachSessionRemarksV2, isPast),
  );
}

function ScheduleXEventCell({
  calendarEvent,
  variant,
  calendarView,
}: {
  calendarEvent: CalendarEventExternal;
  variant: 'month' | 'time-grid' | 'date-grid';
  calendarView?: 'month' | 'week' | 'day';
}) {
  const eventsById = useContext(ScheduleXEventsByIdContext);
  const event: ExpandedEvent | null =
    eventsById.get(String(calendarEvent.id)) ?? expandedEventFromScheduleX(calendarEvent);

  if (!event) {
    return null;
  }

  return (
    <div className={scheduleXEventCellClassName(event, { variant, calendarView })}>
      <EventScheduleAnchor event={event} variant={variant === 'month' ? 'month' : 'default'} />
    </div>
  );
}

function ScheduleXTimeGridEventCell({ calendarEvent }: { calendarEvent: CalendarEventExternal }) {
  const { view } = useContext(EventContext);
  return <ScheduleXEventCell calendarEvent={calendarEvent} variant="time-grid" calendarView={view} />;
}

function ScheduleXDateGridEventCell({ calendarEvent }: { calendarEvent: CalendarEventExternal }) {
  const { view } = useContext(EventContext);
  return <ScheduleXEventCell calendarEvent={calendarEvent} variant="date-grid" calendarView={view} />;
}

function ScheduleXMonthGridEventCell({ calendarEvent }: { calendarEvent: CalendarEventExternal }) {
  return <ScheduleXEventCell calendarEvent={calendarEvent} variant="month" />;
}

function ScheduleXWeekGridDate({ date }: { date: string }) {
  const plainDate = scheduleXPlainDateFromString(date);
  const dateStr = plainDate.toString();
  const weekday = plainDate.toLocaleString('en-US', { weekday: 'short' });
  return (
    <div data-testid={`sessions-calendar-header-${dateStr}`} className="sx-week-grid-date">
      <span className="sx-week-grid-date__number">{plainDate.day}</span>
      <span className="sx-week-grid-date__weekday">{weekday}</span>
    </div>
  );
}

function ScheduleXWeekGridHour({ gridStep }: { hour: number; gridStep: { hour: number; minute: number } }) {
  const timeStr = `${String(gridStep.hour).padStart(2, '0')}-${String(gridStep.minute).padStart(2, '0')}`;
  const showLabel = gridStep.minute === 0 || gridStep.minute === 30;
  const label = showLabel
    ? Temporal.PlainTime.from({ hour: gridStep.hour, minute: gridStep.minute }).toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  const isMajorGridLine = gridStep.minute === 0 || gridStep.minute === 30;

  return (
    <div
      data-testid={`sessions-calendar-time-slot-${timeStr}`}
      className={clsx(
        'sx-week-grid-hour',
        isMajorGridLine ? 'sx-week-grid-hour--major-line' : 'sx-week-grid-hour--sub-step',
      )}
    >
      {label != null && <span className="sx-week-grid-hour__label">{label}</span>}
    </div>
  );
}

function ScheduleXMonthGridDate({ date, jsDate }: { date: number; jsDate: Date }) {
  const dateStr = isoDateFromJsDate(jsDate);
  return (
    <div data-testid={`sessions-calendar-day-slot-${dateStr}`} className="sx-month-grid-date">
      {date}
    </div>
  );
}

/** Replaces built-in header (hidden in schedule-x-calendar.css); toolbar is rendered above the grid. */
function ScheduleXHeaderContent() {
  return null;
}

export const SCHEDULE_X_CUSTOM_COMPONENTS = {
  timeGridEvent: ScheduleXTimeGridEventCell,
  dateGridEvent: ScheduleXDateGridEventCell,
  monthGridEvent: ScheduleXMonthGridEventCell,
  weekGridDate: ScheduleXWeekGridDate,
  weekGridHour: ScheduleXWeekGridHour,
  monthGridDate: ScheduleXMonthGridDate,
  headerContent: ScheduleXHeaderContent,
} as const;
